<template>
  <div class="annotation-panel">
    <!-- Header -->
    <div class="panel-header">
      <div class="panel-header-text">
        <h3 class="panel-title">{{ $t('annotationPanel.title', { n: annotationNumber }) }}</h3>
        <p class="panel-subtitle">
          {{ $t('annotationPanel.createdBy', { date: createdDate, author: userName }) }}
        </p>
      </div>
      <div class="panel-header-actions">
        <button class="action-btn" @click="$emit('delete')">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.25 4.5H15.75M7.5 8.25V13.5M10.5 8.25V13.5M3.75 4.5L4.5 14.25C4.5 15.075 5.175 15.75 6 15.75H12C12.825 15.75 13.5 15.075 13.5 14.25L14.25 4.5M6.75 4.5V2.25C6.75 1.838 7.088 1.5 7.5 1.5H10.5C10.912 1.5 11.25 1.838 11.25 2.25V4.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          v-if="annotation?.isSaved"
          class="action-btn"
          @click="$emit('close')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2L14 14M14 2L2 14"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Thumbnail -->
    <div class="panel-thumbnail">
      <div v-if="!annotation?.croppedImage" class="thumbnail-loading">
        <q-spinner color="white" size="32px" />
      </div>
      <img
        v-else
        :src="annotation.croppedImage"
        class="thumbnail-img"
        alt="Annotation crop"
      />
    </div>

    <!-- Analyzing overlay -->
    <div v-if="annotation?.isAnalyzing" class="analyzing-overlay">
      <q-spinner color="#FA6930" size="36px" />
      <p class="analyzing-text">{{ $t('annotationPanel.analyzing') }}</p>
    </div>

    <!-- Analysis error notice -->
    <div v-else-if="annotation?.analysisError" class="analysis-error-notice">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 5.5V8.5M8 10.5H8.01M14.5 8C14.5 11.5899 11.5899 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <p class="analysis-error-text">{{ $t('annotationPanel.predictionFailed') }}</p>
    </div>

    <!-- Form -->
    <div class="panel-form" :class="{'form-loading': annotation?.isAnalyzing}">
      <!-- Damage type (free text) -->
      <div class="form-field">
        <label class="field-label">{{ $t('annotationPanel.damageTypeLabel') }}</label>
        <input
          v-model="damageType"
          type="text"
          :placeholder="$t('annotationPanel.damageTypeCustomPlaceholder')"
          class="field-input"
          :disabled="annotation?.isAnalyzing"
        />
      </div>

      <!-- Severity level -->
      <div class="form-field">
        <label class="field-label">{{ $t('annotationPanel.severityLabel') }}</label>
        <div class="severity-selector">
          <div
            v-for="level in severityLevels"
            :key="level"
            class="severity-item"
            :class="{selected: severityLevel === level}"
            @click="severityLevel = level"
          >
            <span class="severity-number">{{ level }}</span>
            <div
              class="severity-bar-segment"
              :style="{backgroundColor: severityColors[level]}"
            ></div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="form-field">
        <label class="field-label">{{ $t('annotationPanel.descriptionLabel') }}</label>
        <textarea
          v-model="description"
          class="field-textarea"
          :placeholder="$t('annotationPanel.descriptionPlaceholder')"
          rows="6"
          :disabled="annotation?.isAnalyzing"
        ></textarea>
      </div>

      <!-- Recommended action -->
      <div class="form-field">
        <label class="field-label">{{ $t('annotationPanel.recommendedActionLabel') }}</label>
        <textarea
          v-model="recommendedAction"
          :placeholder="$t('annotationPanel.recommendedActionPlaceholder')"
          class="field-textarea"
          rows="4"
        />
      </div>
    </div>

    <p class="din-disclaimer">{{ $t('annotationPanel.dinDisclaimer') }}</p>

    <!-- Save -->
    <button
      class="save-btn"
      :disabled="annotation?.isAnalyzing"
      @click="handleSave"
    >
      {{ $t('annotationPanel.saveButton') }}
    </button>
  </div>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import {useI18n} from "vue-i18n";
import {QSpinner} from "quasar";
import {authService} from "@/services/authService.js";

const {t} = useI18n();

const props = defineProps({
  annotation: {
    type: Object,
    required: true,
  },
  annotationIndex: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["close", "delete", "save"]);

const SEVERITY_COLORS = ["#8c8c8c", "#52c41a", "#a0d911", "#faad14", "#fa8c16", "#f5222d"];
const SEVERITY_LEVELS = [0, 1, 2, 3, 4, 5];

const damageType = ref("");
const severityLevel = ref(null);
const description = ref("");
const recommendedAction = ref("");

const annotationNumber = computed(() =>
  String(props.annotationIndex + 1).padStart(2, "0"),
);

const createdDate = computed(() => {
  const d = new Date();
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${String(d.getDate()).padStart(2, "0")}.${months[d.getMonth()]} ${d.getFullYear()}`;
});

const userName = computed(() => {
  try {
    return authService.userEmail?.split("@")[0] ?? "";
  } catch {
    return "";
  }
});

const severityColors = SEVERITY_COLORS;
const severityLevels = SEVERITY_LEVELS;

// Pre-fill from saved values, falling back to AI predictions
watch(
  () => props.annotation,
  (ann) => {
    if (!ann) return;
    damageType.value = ann.savedDamageType ?? ann.type ?? "";
    severityLevel.value = ann.savedSeverityLevel ?? ann.severityLevel ?? null;
    description.value = ann.savedDescription ?? ann.description ?? "";
    recommendedAction.value = ann.savedRecommendedAction ?? ann.recommendedAction ?? "";
  },
  {immediate: true},
);

const handleSave = () => {
  emit("save", {
    damageType: damageType.value,
    severityLevel: severityLevel.value,
    description: description.value,
    recommendedAction: recommendedAction.value,
  });
};
</script>

<style lang="scss" scoped>
.annotation-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 280px;
  height: 100%;
  background: var(--color-dark-bg-primary);
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .panel-title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 700;
    color: var(--color-white);
  }

  .panel-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--color-text-placeholder);
  }
}

.panel-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  background: none;
  border: none;
  color: var(--color-text-placeholder);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-white);
  }
}

.panel-thumbnail {
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  background: var(--color-dark-bg-secondary);
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  .thumbnail-img {
    width: 100%;
    height: auto;
    display: block;
    border: 2px solid var(--color-gree300);
    border-radius: 6px;
  }

  .thumbnail-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    width: 100%;
  }
}

.panel-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .field-label {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-white);
  }
}

.field-input {
  width: 100%;
  background: var(--color-dark-bg-secondary);
  border: 1px solid var(--color-border-divider);
  border-radius: 6px;
  padding: 11px 14px;
  color: var(--color-white);
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.2s ease;
  margin-top: 6px;

  &::placeholder {
    color: var(--color-text-placeholder);
  }

  &:focus {
    border-color: var(--color-gree300);
  }
}

.severity-selector {
  display: flex;
  gap: 4px;

  .severity-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    .severity-number {
      font-size: 14px;
      font-weight: 500;
      color: var(--color-text-placeholder);
      transition: color 0.15s ease;
    }

    .severity-bar-segment {
      width: 100%;
      height: 6px;
      border-radius: 3px;
      opacity: 0.4;
      transition: opacity 0.15s ease;
    }

    &.selected,
    &:hover {
      .severity-number {
        color: var(--color-white);
        font-weight: 700;
      }

      .severity-bar-segment {
        opacity: 1;
      }
    }
  }
}

.field-textarea {
  width: 100%;
  background: var(--color-dark-bg-secondary);
  border: 1px solid var(--color-border-divider);
  border-radius: 6px;
  padding: 12px 14px;
  color: var(--color-white);
  font-size: 14px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--color-text-placeholder);
  }

  &:focus {
    border-color: var(--color-gree300);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.analyzing-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 0;

  .analyzing-text {
    margin: 0;
    font-size: 14px;
    color: var(--color-text-placeholder);
  }
}

.analysis-error-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(245, 34, 45, 0.12);
  color: #f5222d;

  .analysis-error-text {
    margin: 0;
    font-size: 13px;
  }
}

.form-loading {
  opacity: 0.4;
  pointer-events: none;
}

.din-disclaimer {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--color-text-secondary);
}

.save-btn {
  width: 100%;
  padding: 14px;
  background: var(--color-gree300);
  border: none;
  border-radius: 6px;
  color: var(--color-dark-bg-primary);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  margin-top: auto;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
</style>
